import { HttpEventType } from "@angular/common/http";
import { Component } from "@angular/core";
import { AppConfig } from '../../../../app/app.config';
import { ToastController, ViewController } from "ionic-angular";
import { DprAttachmentFile } from "../../../../models/dpr-attachments/dpr-attachments.models";
import { PtLuxonDate } from '../../../../models/projects/date.models';
import { NgxFileDropEntry } from "ngx-file-drop";
import { DailyLogService } from "../../../../services/daily-log/daily-log.service";
import { DprAttachmentFileUploadService } from "../../../../services/dpr-attachments/dpr-attachments-file-upload.service";
import { DateTime } from "luxon";
import { LockDayFilters } from "../../../../models/lock-day.models";
import { LockedDayService } from "../../../../services/locked-day/locked-day.service";
import { CompleteDayService } from "../../../../services/complete-day/complete-day.service";

@Component({
  selector: 'app-dpr-attachments-edit-component',
  templateUrl: 'dpr-attachments-edit-component.html',
})
export class DprAttachmentsEditComponentComponent {

    config = AppConfig.settings;
    loading: boolean;
    fileUploadingPercent: number;
    uploadedFiles: DprAttachmentFile[] = [];
    showAlert: boolean;
    alertText: string;
    dailyLogDate: PtLuxonDate = new PtLuxonDate();
    displayDate: string;
    
    //day completed related
    dayLockedByDate: boolean = false;
    lockable: boolean = false;
    dayCompleted: boolean;
    isImplementerActiveForDay: boolean;

    constructor(
        private dprAttachmentFileUploadService: DprAttachmentFileUploadService,
        private toastCtrl: ToastController,
        public dailyLogService: DailyLogService,
        public lockedDayService: LockedDayService,
        private completeDayService: CompleteDayService,
    ){}

    async ngOnInit() {
        this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();
        this.displayDate = this.dailyLogDate.dateTime.toFormat('EEEE, dd MMMM y');
        await this.loadFileList();
        await this.checkDayLocked();
    }

    public onFileClick(uploadedFile: DprAttachmentFile): void {
        if (!uploadedFile || !uploadedFile.guid) {
        return;
        }

        this.loading = true;
        this.dprAttachmentFileUploadService
        .downloadFile(uploadedFile.guid)
        .subscribe((blob: Blob) => {
            const file = new Blob([blob], { type: 'application/pdf' });
            const fileURL = URL.createObjectURL(file);

            const link = document.createElement('a');
            link.href = fileURL;
            link.download = uploadedFile.name + uploadedFile.extension;
            link.click();

            this.loading = false;
        });
    }

    onFileRemove(item: any) {
        this.loading = true;
        this.dprAttachmentFileUploadService.deleteFile(item.guid).subscribe({
        next: (res) => {
            const index = this.uploadedFiles.findIndex(
            (f) => f.guid === item.guid
            );

            if (index === -1) {
            return;
            }

            this.uploadedFiles.splice(index, 1);
            this.presentDeleteToast();
            this.loading = false;
        }
        });


    }

    fileDropped(event: NgxFileDropEntry[]) {
        if (this.isReadOnly()) {
            return;
        }
        this.showAlert = false;

        for (const file of event) {
        const allowed = this.checkAllowedFileExtension(file.fileEntry.name);

        if (file.fileEntry.isFile && allowed) {
            const fileEntry = file.fileEntry as FileSystemFileEntry;

            fileEntry.file(async (f: File) => {
                this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();
                this.dprAttachmentFileUploadService.uploadFile(f, this.dailyLogDate).subscribe({
                    next: (uploadEvent) => {
                    this.fileUploadingPercent = 0;
                    switch (uploadEvent.type) {
                        case HttpEventType.UploadProgress:
                        this.fileUploadingPercent = Math.round(
                            (uploadEvent.loaded / uploadEvent.total) * 100
                        );
                        break;
                        case HttpEventType.Response:
                        const responseBody = uploadEvent.body as DprAttachmentFile;
                        this.uploadedFiles.push(responseBody);

                        this.presentUploadToast();

                        this.fileUploadingPercent = -1;
                        break;
                    }
                    },
                    error: (err) => {
                    if (err.status === 413) {
                        this.alertText = "File size too large. Max size is 5MB";
                        this.showAlert = true;
                    } else {
                        console.log(err);
                        this.alertText = "An error occurred.";
                        this.showAlert = true;
                    }

                    this.fileUploadingPercent = -1;
                    },
                    complete: () => {
                    this.fileUploadingPercent = -1;
                    },
                });
            });
        } else if (!allowed) {
            this.showAlert = true;
            this.alertText = "Incorrect file type. File must be .pdf"
        }
        }
    }

    private checkAllowedFileExtension(filename: string) {
        if (!filename) {
        return false;
        }

        const extension = filename.split('.').pop();

        return extension.toLowerCase() === 'pdf';
    }

    private async loadFileList() {
        this.loading = true;
        this.dprAttachmentFileUploadService.getUploadedFiles(this.dailyLogDate).subscribe({
            next: (res) => {
                this.uploadedFiles = res;
                this.sortUploadedFiles();
                this.loading = false;
            },
            error: (err) => {
                    console.log(err);
            }
        });
    }

    private presentUploadToast(): Promise<any> {
        const toast = this.toastCtrl.create({
        message: 'File Uploaded',
        duration: 3000,
        position: 'bottom'
        });

        return toast.present();
    }

    private presentDeleteToast(): Promise<any> {
        const toast = this.toastCtrl.create({
        message: 'File Removed',
        duration: 3000,
        position: 'bottom'
        });

        return toast.present();
    }

    private sortUploadedFiles(desc: boolean = true): void {
        this.uploadedFiles.sort((a, b) => {
            const aTime = a?.uploadDate instanceof DateTime ? a.uploadDate.toMillis() : 0;
            const bTime = b?.uploadDate instanceof DateTime ? b.uploadDate.toMillis() : 0;

            return desc ? bTime - aTime : aTime - bTime;
        });
    }

    public isReadOnly(): boolean {
        return !!this.dayCompleted;
    }

    async checkDayLocked() {
        const filters: LockDayFilters = {
        date: this.dailyLogDate.dateTime,
        userId: null,
        history: null
        };

        const lockedSummary = await this.lockedDayService.checkDay(filters);

        this.dayLockedByDate = lockedSummary.lockedByDate;
        this.lockable = lockedSummary.lockable;

        this.completeDayService.checkDay(filters).then(result => {
        this.dayCompleted = result;
        });
    }
}



// WEBPACK FOOTER //
// ./src/pages/DPR Pages/dpr-attachments/dpr-attachments-edit-component/dpr-attachments-edit-component.ts